import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, TextField, Paper, Table, TableBody, FormGroup, FormControlLabel, Chip, Stack, Tooltip,
    Checkbox, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, InputAdornment,
    DialogActions, IconButton, FormControl, FormLabel, FormHelperText, Switch, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import VpnKey from '@mui/icons-material/VpnKey';
import { useSnackbar } from '../context/SnackbarContext';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../Auth';
import api from '../api/axiosInstance';
import { generateSecurePassword } from '../utils/validation';

export default function TenantManagement() {
    const [tenants, setTenants] = useState([]);
    const [open, setOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingTenantId, setEditingTenantId] = useState(null);
    const [newTenant, setNewTenant] = useState({
        name: '',
        schema_name: '',
        enabled_modules: [],
        wwcc_limit: '25',
        adminUsername: '',
        adminEmail: '',
        adminPassword: '',
        sendWelcomeEmail: true
    });
    const [successData, setSuccessData] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const showSnackbar = useSnackbar();
    const { user, setUser, refreshGlobalTenants, switchTenant } = useAuth();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tenantToDelete, setTenantToDelete] = useState(null);
    const [confirmName, setConfirmName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [limitType, setLimitType] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleModuleToggle = (module) => {
        const current = newTenant.enabled_modules;
        const updated = current.includes(module)
            ? current.filter(m => m !== module)
            : [...current, module];
        setNewTenant({ ...newTenant, enabled_modules: updated });
    };

    const filteredTenants = tenants.filter(t =>
        t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.schema_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const MODULE_COLOURS = {
        awards: '#0400ff',    //blue
        wwcc: '#fbc02d'       //yellow
    };

    /**
     * LOGIC UPDATE: 
     * If 2+ words, use first letter of each word.
     * If 1 word, use first 8 chars.
     */
    const generateSchemaSuggestion = (name, existingTenants) => {
        if (!name) return '';

        // 1. Generate the base initials
        const words = name.trim().split(/\s+/).filter(w => w.length > 0);
        let base = '';
        if (words.length >= 2) {
            base = words.map(word => word[0]).join('').toLowerCase();
        } else {
            base = words[0].toLowerCase().slice(0, 8);
        }
        base = base.replace(/[^a-z0-9]/g, '');

        // 2. Find the highest existing number for this base
        // We look for schemas like "tcc1", "tcc2", etc.
        let maxNum = 0;
        existingTenants.forEach(t => {
            const match = t.schema_name.match(new RegExp(`^${base}(\\d+)$`));
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxNum) maxNum = num;
            }
        });

        // 3. Return the base + (highest number + 1)
        return `${base}${maxNum + 1}`;
    };


    // Wrap fetchTenants in useCallback
    const fetchTenants = useCallback(async () => {
        try {
            const res = await api.get('/tenants');
            setTenants(res.data);
        } catch (err) {
            showSnackbar('Failed to load tenants', 'error');
        }
    }, [showSnackbar]); // showSnackbar is a stable dependency from context

    useEffect(() => {
        fetchTenants();
    }, [fetchTenants]);

    const handleNameChange = (e) => {
        const val = e.target.value;
        if (isEditMode) {
            setNewTenant({ ...newTenant, name: val });
        } else {
            setNewTenant({
                ...newTenant,
                name: val,
                schema_name: generateSchemaSuggestion(val, tenants)
            });
        }
    };


    const handleSaveTenant = async () => {
        setLoading(true);
        try {
            if (isEditMode) {
                // PUT request
                console.log(`Tenant PUT request`);
                await api.put(`/tenants/${editingTenantId}`, newTenant);

                // SYNC LOCAL STORAGE: If the edited tenant is the one the user is currently in
                if (user?.tenant_id === editingTenantId) {
                    const updatedUser = {
                        ...user,
                        tenant_name: newTenant.name
                    };

                    // Update Local Storage
                    localStorage.setItem('user', JSON.stringify(updatedUser));

                    // Update Auth Context (This triggers the re-render in Dashboard.js)
                    // Assuming your useAuth hook provides a way to update the user
                    if (typeof setUser === 'function') {
                        setUser(updatedUser);
                    }
                }

                showSnackbar('Tenant updated successfully!', 'success');
            } else {
                // POST request
                console.log(`Tenant POST request`);
                await api.post('/tenants', newTenant);
                console.log(`Tenant POST done`);

                console.log(`About to set success data`);
                setSuccessData({
                    username: newTenant.adminUsername,
                    password: newTenant.adminPassword,
                    tenantName: newTenant.name
                });

                console.log(`About to open success dialog`);
                setShowSuccessDialog(true);
            }

            handleCloseDialog();
            fetchTenants();
            if (refreshGlobalTenants) refreshGlobalTenants();

        } catch (err) {
            // Axios puts the server's error message in err.response.data
            const status = err.response?.status;
            const serverError = err.response?.data?.error || '';

            if (status === 409 || serverError.includes('already exists')) {
                showSnackbar(`The schema "${newTenant.schema_name}" is already taken.`, 'error');
            } else {
                showSnackbar(serverError || 'Failed to save tenant', 'error');
            }
        } finally {
            setLoading(false);
        }
    };


    const handleOpenCreate = () => {
        setIsEditMode(false);
        setNewTenant({
            name: '',
            schema_name: '',
            enabled_modules: [],
            adminUsername: '',
            adminEmail: '',
            adminPassword: '',
            wwcc_limit: 25,
            sendWelcomeEmail: true
        });
        setOpen(true);
    };

    const confirmDelete = async () => {
        try {
            // Axios DELETE
            await api.delete(`/tenants/${tenantToDelete.id}/${tenantToDelete.schema_name}`);

            showSnackbar('Tenant and all associated data purged.');
            setDeleteDialogOpen(false);
            setConfirmName('');

            await fetchTenants();
            // This clears the tenant_id from your Auth context/state
            await switchTenant(null);

            if (refreshGlobalTenants) refreshGlobalTenants();

            // Navigate to the base admin path to ensure the UI resets
            navigate('/admin/tenants');

        } catch (err) {
            showSnackbar(err.response?.data?.error || 'Server error during deletion', 'error');
        }
    };

    const handleEditClick = (tenant) => {
        setIsEditMode(true);
        setEditingTenantId(tenant.id);
        setNewTenant({
            name: tenant.name,
            schema_name: tenant.schema_name, // Should be disabled in UI
            enabled_modules: tenant.enabled_modules || [],
            wwcc_limit: tenant.wwcc_limit,
        });
        // Check if current limit is a preset
        const presets = ['25', '75', '200', '500'];
        if (presets.includes(tenant.wwcc_limit?.toString())) {
            setLimitType(tenant.wwcc_limit.toString());
        } else {
            setLimitType('custom');
        }
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEditMode(false);
        setEditingTenantId(null);
        setNewTenant({ name: '', schema_name: '', enabled_modules: [] });
    };

    const handleSwitchTenant = async (tenantId) => {
        const result = await switchTenant(tenantId);
        if (result?.success) {
            navigate(result.target);
        }
    };


    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Global Tenant Management</Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Register new tenants and automatically provision isolated database schemas.
            </Typography>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    sx={{ height: 40, textTransform: 'none' }}
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                >
                    Register New Tenant
                </Button>

                <TextField
                    size="small"
                    placeholder="Search tenants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 300 }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell><strong>Tenant Name</strong></TableCell>
                            <TableCell><strong>Schema</strong></TableCell>
                            <TableCell><strong>Modules</strong></TableCell>
                            <TableCell><strong>WWCC limit</strong></TableCell>
                            <TableCell><strong>Created</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTenants.length > 0 ? (
                            filteredTenants.map((t) => (
                                <TableRow key={t.id}>
                                    <TableCell>
                                        <Button
                                            onClick={() => handleSwitchTenant(t.id)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 'bold',
                                                padding: 0,
                                                textAlign: 'left',
                                                justifyContent: 'flex-start',
                                                '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' }
                                            }}
                                        >
                                            {t.name}
                                        </Button>
                                    </TableCell>
                                    <TableCell><code>{t.schema_name}</code></TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {(t.enabled_modules || []).map((m) => {
                                                const colorValue = MODULE_COLOURS[m] || 'default';
                                                const isThemeColor = ['primary', 'secondary', 'error', 'info', 'success', 'warning', 'default'].includes(colorValue);

                                                return (
                                                    <Chip
                                                        key={m}
                                                        label={m.toUpperCase()}
                                                        size="small"
                                                        variant="outlined"
                                                        // Only pass theme keys to the color prop
                                                        color={isThemeColor ? colorValue : 'default'}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            // If it's a custom color, apply it to the border and text
                                                            ...(!isThemeColor && {
                                                                color: colorValue,
                                                                borderColor: colorValue,
                                                                '&:hover': {
                                                                    backgroundColor: `${colorValue}11`, // Light transparency on hover
                                                                }
                                                            })
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {/* Only show the limit if the WWCC module is actually enabled */}
                                        {t.enabled_modules?.includes('wwcc') ? (
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {t.wwcc_limit}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(t.created_at)}</TableCell>
                                    <TableCell align="right">
                                        <IconButton color="primary" onClick={() => handleEditClick(t)} sx={{ mr: 1 }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => { setTenantToDelete(t); setDeleteDialogOpen(true); }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {searchTerm ? `No tenants found matching "${searchTerm}"` : "No tenants available."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>


                </Table>
            </TableContainer>

            {/* FORM DIALOG (Used for Create & Edit) */}
            <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>{isEditMode ? 'Edit Tenant' : 'Provision New Tenant'}</DialogTitle>
                <DialogContent>
                    <TextField label="Tenant Name" required fullWidth margin="normal" value={newTenant.name} onChange={handleNameChange} />
                    <TextField
                        label="Schema Name"
                        fullWidth
                        margin="normal"
                        value={newTenant.schema_name}
                        disabled={isEditMode} // LOCK SCHEMA NAME ON EDIT
                        onChange={(e) => setNewTenant({ ...newTenant, schema_name: e.target.value.toLowerCase() })}
                        helperText={isEditMode ? "Schema name cannot be changed." : "Suffix will be auto-incremented if name exists."}
                    />
                    <FormControl
                        required
                        component="fieldset"
                        variant="standard"
                        sx={{ mt: 2, display: 'block' }}
                    >
                        <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.85rem' }}>
                            Enabled Modules
                        </FormLabel>

                        <FormGroup row>
                            <FormControlLabel
                                control={<Checkbox checked={newTenant.enabled_modules.includes('awards')} onChange={() => handleModuleToggle('awards')} />}
                                label="Awards"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={newTenant.enabled_modules.includes('wwcc')} onChange={() => handleModuleToggle('wwcc')} />}
                                label="WWCC"
                            />
                        </FormGroup>

                        {newTenant.enabled_modules.length === 0 && (
                            <FormHelperText>At least one module must be selected</FormHelperText>
                        )}
                    </FormControl>

                    {newTenant.enabled_modules?.includes('wwcc') && (
                        <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <FormControl sx={{ minWidth: 150 }}>
                                    <FormLabel sx={{ fontSize: '0.75rem', mb: 1 }}>WWCC Record Limit</FormLabel>
                                    <TextField
                                        select
                                        size="small"
                                        value={limitType}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setLimitType(val);
                                            if (val !== 'custom') {
                                                setNewTenant({ ...newTenant, wwcc_limit: parseInt(val) });
                                            }
                                        }}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="25">25 Records</option>
                                        <option value="75">75 Records</option>
                                        <option value="200">200 Records</option>
                                        <option value="500">500 Records</option>
                                        <option value="custom">Custom Amount...</option>
                                    </TextField>
                                </FormControl>

                                {limitType === 'custom' && (
                                    <>
                                        <FormControl sx={{ minWidth: 150 }}>
                                            <FormLabel sx={{ fontSize: '0.75rem', mb: 1 }}>Total allowed rows</FormLabel>
                                            <TextField
                                                label="Enter Limit"
                                                type="number"
                                                size="small"
                                                sx={{ width: 150 }}
                                                value={newTenant.wwcc_limit || ''}
                                                onChange={(e) => setNewTenant({
                                                    ...newTenant,
                                                    wwcc_limit: parseInt(e.target.value) || 0
                                                })}
                                            />
                                        </FormControl>
                                    </>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* --- ADMIN SECTION: Only rendered when creating a new tenant --- */}
                    {!isEditMode && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Admin Account for this tenant:</Typography>
                            <Stack spacing={2}>
                                <TextField
                                    label="Admin Username"
                                    required
                                    fullWidth
                                    value={newTenant.adminUsername}
                                    onChange={(e) => setNewTenant({ ...newTenant, adminUsername: e.target.value })}
                                />
                                <TextField
                                    label="Admin Email"
                                    fullWidth
                                    placeholder="e.g. manager@school.edu.au"
                                    value={newTenant.adminEmail}
                                    onChange={(e) => setNewTenant({ ...newTenant, adminEmail: e.target.value })}
                                />
                                <TextField
                                    label="Admin Password"
                                    required
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    value={newTenant.adminPassword}
                                    onChange={(e) => setNewTenant({ ...newTenant, adminPassword: e.target.value })}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Helpful Password Suggestion">
                                                    <IconButton
                                                        onClick={() => setNewTenant({
                                                            ...newTenant,
                                                            adminPassword: generateSecurePassword()
                                                        })}
                                                        edge="end"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <VpnKey fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton
                                                    onClick={togglePasswordVisibility}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
                    {!isEditMode && (
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between', // Pushes label left, switch right
                            alignItems: 'center'
                        }}>
                            <FormControlLabel
                                labelPlacement="start"
                                control={
                                    <Switch
                                        checked={newTenant.sendWelcomeEmail}
                                        onChange={(e) => setNewTenant({ ...newTenant, sendWelcomeEmail: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Send Welcome Email
                                    </Typography>
                                }
                                /* Removing margins to ensure it touches the container edge */
                                sx={{ ml: 0, mr: 0, width: '100%', justifyContent: 'space-between' }}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
                        <Button
                            sx={{ height: 40, textTransform: 'none' }}
                            onClick={handleCloseDialog}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveTenant}
                            sx={{ height: 40, textTransform: 'none' }}
                            disabled={
                                loading ||
                                !newTenant.name?.trim() ||
                                newTenant.enabled_modules.length === 0 ||
                                (!isEditMode && (
                                    !newTenant.adminUsername?.trim() ||
                                    !newTenant.adminPassword?.trim()
                                ))
                            }
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {loading ? (isEditMode ? 'Updating...' : 'Provisioning...') : (isEditMode ? 'Update' : 'Provision Tenant')}
                        </Button>
                    </Box>

                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG (Existing logic renamed to tenant) */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ color: 'error.main' }}>Permanent Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Confirm deletion of <strong>{tenantToDelete?.name}</strong>.</Typography>
                    <TextField fullWidth label={`Type "${tenantToDelete?.schema_name}"`} value={confirmName} onChange={(e) => setConfirmName(e.target.value)} color="error" sx={{ mt: 2 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" disabled={confirmName !== tenantToDelete?.schema_name} onClick={confirmDelete}>Purge Tenant</Button>
                </DialogActions>
            </Dialog>

            {/* SUCCESS DIALOG (New tenant admin user and password displayed) */}
            <Dialog
                open={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>✅ Provisioning Complete</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Tenant <strong>{successData?.tenantName}</strong> has been created.
                        Please make a note of these admin credentials:
                    </Typography>

                    <Box sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300'
                    }}>
                        <Typography variant="caption" display="block" color="text.secondary">Username</Typography>
                        <Typography variant="body1" sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            letterSpacing: 1,
                            mb: 1
                        }}>
                            {successData?.username || ''}
                        </Typography>

                        <Typography variant="caption" display="block" color="text.secondary">Password</Typography>
                        <Typography variant="body1" sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            letterSpacing: 1
                        }}>
                            {successData?.password || ''}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            navigator.clipboard.writeText(`User: ${successData.username}\nPass: ${successData.password}`);
                            showSnackbar('Copied to clipboard!');
                        }}
                        color="primary"
                    >
                        Copy Details
                    </Button>
                    <Button onClick={() => setShowSuccessDialog(false)} variant="contained">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}